import React, { useState, useEffect } from "react";
import {
  DeductHalfDayFromAttendance,
  getAttendenceData,
  insertMarkAttendence,
} from "../../services/dataService";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { getSecureItem } from "../../services/SecureStorage";

// Helper functions (copied from Attendance component)
const isDefaultTime = (time) => {
  return (
    time === "1900-01-01T00:00:00.000Z" ||
    time === null ||
    time === undefined ||
    time === ""
  );
};

const getTimeInMinutes = (dateString) => {
  if (!dateString) return 0;
  const date = new Date(dateString);
  return date.getUTCHours() * 60 + date.getUTCMinutes();
};

const isLateArrival = (row) => {
  if (!row.PUNCHIN || !row.StartTime || !row.RECORDDATE) return false;
  const gracePeriod = parseInt(row.GracePeriod) || 0;
  const punchInMinutes = getTimeInMinutes(row.PUNCHIN);
  const startTimeMinutes = getTimeInMinutes(row.StartTime);
  return punchInMinutes > startTimeMinutes + gracePeriod;
};

const calculateLateTime = (row) => {
  if (!row.PUNCHIN || !row.StartTime) return "-";
  const gracePeriod = parseInt(row.GracePeriod) || 0;
  const punchInMinutes = getTimeInMinutes(row.PUNCHIN);
  const startTimeMinutes = getTimeInMinutes(row.StartTime);
  const allowedStartMinutes = startTimeMinutes + gracePeriod;
  if (punchInMinutes <= allowedStartMinutes) return "-";
  const lateMinutes = punchInMinutes - allowedStartMinutes;
  const hours = Math.floor(lateMinutes / 60);
  const minutes = lateMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const EarlyOut = (row) => {
  if (!row.PUNCHOUT || row.PUNCHOUT === "1900-01-01T00:00:00.000Z") return "-";

  const endTimeStr = row.EndTime?.split("T")[1]?.split(".")[0];
  const punchOutStr = row.PUNCHOUT?.split("T")[1]?.split(".")[0];

  if (!endTimeStr || !punchOutStr) return "-";

  const gracePeriod = parseInt(row.GracePeriod) || 0;

  const toMinutes = (timeStr) => {
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  };

  const endMinutes = toMinutes(endTimeStr);
  const punchOutMinutes = toMinutes(punchOutStr);

  // Allow employee to leave up to GracePeriod minutes early
  const allowedPunchOutMinutes = endMinutes - gracePeriod;

  if (punchOutMinutes >= allowedPunchOutMinutes) return "-";

  const earlyOutMinutes = allowedPunchOutMinutes - punchOutMinutes;

  const hours = Math.floor(earlyOutMinutes / 60);
  const minutes = earlyOutMinutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

function AttendancePending() {
  const UID = getSecureItem("userId");

  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedProfitCenter, setSelectedProfitCenter] = useState("");
  const [departments, setDepartments] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [individualCheck, setIndividualcheck] = useState([]);
  const departmentIds = getSecureItem("DepartmentId") || "";
  const DepartmentId = departmentIds.split(",")[0] || null;
  const [LateDeductHalfday, setlateDeductHalfday] = useState("");

  const isAdmin = getSecureItem("IsAdmin");

  // Get yesterday's date as default
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };

  const getMaxDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };

  const [selectedDate, setSelectedDate] = useState(getYesterday());

  const fetchAttendanceRecords = async (date) => {
    try {
      setLoading(true);

      const payload = {
        Date: date || selectedDate,
        UserId: UID,
      };

      const response = await getAttendenceData(payload);

      setlateDeductHalfday(response.data.LateDeductHalfday);

      let dataArray = [];

      if (Array.isArray(response)) {
        dataArray = response;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        dataArray = response.data.data;
      } else if (response && typeof response === "object") {
        const possibleArrays = Object.values(response).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          dataArray = possibleArrays[0];
        }
      }

      const pending = dataArray.filter((el) => el.ATTENDANCESTATUS === 0);

      const attendanceDetails =
        isAdmin == 1
          ? pending
          : pending.filter((el) => el?.DEPARTMENTID == DepartmentId);

      setAttendanceData(attendanceDetails);

      const initialCheckData = pending.map((item) => ({
        ...item,
        ATTENDANCESTATUS: 0,
      }));
      setIndividualcheck(initialCheckData);

      const uniqueDepartments = [
        ...new Set(pending.map((item) => item.DepartmentName).filter(Boolean)),
      ];
      setDepartments(uniqueDepartments);

      const uniqueProfitCenters = [
        ...new Set(
          pending.map((item) => item.ProfitcenterName).filter(Boolean),
        ),
      ];
      setProfitCenters(uniqueProfitCenters);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setAttendanceData([]);
      setIndividualcheck([]);
      setDepartments([]);
      setProfitCenters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceRecords(selectedDate);
  }, []);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    fetchAttendanceRecords(newDate);
  };

  // Filter data based on search term, department, and profit center
  useEffect(() => {
    let filtered = attendanceData;

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.EmployeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.EmpCode?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (selectedDepartment) {
      filtered = filtered.filter(
        (item) => item.DepartmentName === selectedDepartment,
      );
    }

    if (selectedProfitCenter) {
      filtered = filtered.filter(
        (item) => item.ProfitcenterName === selectedProfitCenter,
      );
    }

    setFilteredData(filtered);
  }, [attendanceData, searchTerm, selectedDepartment, selectedProfitCenter]);

  const calculateOvertimeMinutes = (row) => {
    if (!row) return 0;

    // Calculate actual minutes from PUNCHIN and PUNCHOUT difference
    let actual = 0;
    if (row.PUNCHIN && row.PUNCHOUT) {
      try {
        const inTime = new Date(row.PUNCHIN);
        const outTime = new Date(row.PUNCHOUT);
        actual = Math.round((outTime - inTime) / (1000 * 60)); // ms to minutes
      } catch {}
    }

    // Min working hour – parse time from date carrier
    let minWorking = 0;
    if (row.MinWorkingHour) {
      try {
        const t = row.MinWorkingHour.split("T")[1]?.split(".")[0] || "00:00:00";
        const [h, m] = t.split(":").map(Number);
        minWorking = (h || 0) * 60 + (m || 0);
      } catch {}
    }

    const potentialOT = actual - minWorking;

    return potentialOT > 0 ? potentialOT : 0;
  };

  const pendingCount = filteredData.filter(
    (record) => record.ATTENDANCESTATUS === 0,
  ).length;

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    const time = new Date(timeString);
    return time.toISOString().substr(11, 5);
  };

  const hasValidPunchTimes = (record) => {
    const punchIn = record.PUNCHIN;
    const punchOut = record.PUNCHOUT;
    const LeaveStatus = record.LeaveStatus;

    if (LeaveStatus == 1) return false;

    const isInvalidTime = (time) => {
      if (!time || time === "" || time === null) return true;
      if (time.includes("1900-01-01")) return true;
      return false;
    };

    if (record.StartTime == record.EndTime) {
      return !isInvalidTime(punchIn) || !isInvalidTime(punchOut);
    } else {
      return !isInvalidTime(punchIn) && !isInvalidTime(punchOut);
    }
  };

  const isSelectAllPunchedChecked = () => {
    const punchedItems = filteredData.filter((item) =>
      hasValidPunchTimes(item),
    );
    if (punchedItems.length === 0) return false;

    const result = punchedItems.every((filteredItem) => {
      const checkItem = individualCheck.find(
        (item) => item.ID == filteredItem.ID,
      );
      return checkItem && checkItem.ATTENDANCESTATUS === 1;
    });

    return result;
  };

  const handleSelectAllPunched = (e) => {
    const isChecked = e.target.checked;

    const updatedData = individualCheck.map((item) => {
      const isInFilteredData = filteredData.some(
        (filtered) => filtered.ID == item.ID,
      );

      if (isInFilteredData) {
        if (hasValidPunchTimes(item)) {
          return {
            ...item,
            ATTENDANCESTATUS: isChecked ? 1 : 0,
          };
        } else {
          return {
            ...item,
            ATTENDANCESTATUS: isChecked ? 0 : 0,
          };
        }
      }
      return item;
    });

    setIndividualcheck(updatedData);
  };

  const handleIndividualCheck = (e) => {
    const checkedItemId = e.target.value;
    const isChecked = e.target.checked;

    const updatedData = individualCheck.map((item) => {
      if (item.ID == checkedItemId) {
        return {
          ...item,
          ATTENDANCESTATUS: isChecked ? 1 : 0,
        };
      }
      return item;
    });

    setIndividualcheck(updatedData);
  };

  const getItemCheckedStatus = (itemId) => {
    const item = individualCheck.find((item) => item.ID == itemId);
    return item ? item.ATTENDANCESTATUS === 1 : false;
  };

  // ================================================================
  // Updated handleAppproveAttendacne with Direct Late Deduct Half-Day logic
  // ================================================================
  const handleAppproveAttendacne = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const checkedItems = individualCheck.filter(
        (item) => item.ATTENDANCESTATUS === 1,
      );

      if (checkedItems.length === 0) {
        toast.error("Please select at least one attendance record to approve.");
        return;
      }

      const hasLate = (record) => {
        const lateStr = calculateLateTime(record);
        if (lateStr === "-") return false;
        const [hours, minutes] = lateStr.split(":").map(Number);
        return hours > 0 || minutes > 0;
      };

      const hasEarly = (record) => {
        const earlyStr = EarlyOut(record);
        if (earlyStr === "-") return false;
        const [hours, minutes] = earlyStr.split(":").map(Number);
        return hours > 0 || minutes > 0;
      };
      const data = {
        UserId: UID,
        AttendenceRecords: checkedItems.map((record) => {
          const isLate = hasLate(record);
          const isEarly = hasEarly(record);

          const currentLate = Number(record.LateCount);
          const currentEarly = Number(record.EarlyCount);

          let lateFlag = "1";
          let earlyFlag = "1";
          let totalDeduction = 0;

          if (isLate && currentLate !== 1) {
            lateFlag = "0";
            totalDeduction++;
          }

          if (isEarly && currentEarly !== 1) {
            earlyFlag = "0";
            totalDeduction++;
          }

          return {
            ...record,
            overtimeMinutes:
              record.overtimeMinutes ?? calculateOvertimeMinutes(record),
            ID: record.ID == null ? "" : record.ID,
            IsMannual: "1",
            ATTENDANCESTATUS: 1,
            EarlyCount: earlyFlag,
            LateCount: lateFlag,
            deductHalfdayTotal:
              totalDeduction > 0 ? String(totalDeduction) : "",
          };
        }),
      };

      const response = await insertMarkAttendence(data);
      if (response.data.data.StatusCode == 200) {
        if (LateDeductHalfday == "0") {
          const recordsWithHalfDay = data.AttendenceRecords.filter(
            (record) => record.deductHalfdayTotal > 0,
          );
          if (recordsWithHalfDay.length > 0) {
            const halfDayData = {
              ...data,
              AttendenceRecords: recordsWithHalfDay,
            };
            await DeductHalfDayFromAttendance(halfDayData);
          }
        }
        toast.success(
          response.data.data.message || "Attendance approved successfully",
        );
        fetchAttendanceRecords(selectedDate);
      } else {
        toast.error(response.data.data.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error occurred while approving attendance:", error);
      toast.error("Error occurred while approving attendance");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Attendance Approval Pendings
        </h2>
        <p
          style={{
            fontSize: "15px",
            marginTop: "6px",
            fontWeight: LateDeductHalfday == 0 ? "bold" : "normal",
            color: LateDeductHalfday == 0 ? "red" : "green",
          }}
        >
          {LateDeductHalfday == 0
            ? "Direct Late Deduct Half-Day is Enabled"
            : "Direct Late Deduct Half-Day is Disabled"}
        </p>
        <Link to={"/"}>
          <button className="btn btn-danger">Back to Dashboard</button>
        </Link>
      </div>

      {!loading && attendanceData.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredData.length} of {attendanceData.length} records
          <span className="ml-4">| Pending Approvals: {pendingCount}</span>
        </div>
      )}

      {/* Controls Section */}
      <div className="mb-6 bg-gray-50 p-4 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              max={getMaxDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search (Name/Code)
            </label>
            <input
              type="text"
              placeholder="Enter name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Department Filter */}
          {isAdmin == 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Departments</option>
                {departments.map((dept, index) => (
                  <option key={index} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Profit Center Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profit Center
            </label>
            <select
              value={selectedProfitCenter}
              onChange={(e) => setSelectedProfitCenter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Profit Centers</option>
              {profitCenters.map((center, index) => (
                <option key={index} value={center}>
                  {center}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading attendance records...</p>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelectAllPunchedChecked()}
                      onChange={handleSelectAllPunched}
                      className="mr-2"
                    />
                    Select All Punched
                  </label>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sl No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                {isAdmin == 1 && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shift Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Punch In
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Punch Out
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {attendanceData.length === 0
                      ? "No attendance records found for the selected date."
                      : "No records match the selected filters."}
                  </td>
                </tr>
              ) : (
                filteredData?.map((record, index) => {
                  const hasValidPunches = hasValidPunchTimes(record);
                  const punchInValid =
                    record.PUNCHIN &&
                    record.PUNCHIN !== "" &&
                    !record.PUNCHIN.includes("1900-01-01");
                  const punchOutValid =
                    record.PUNCHOUT &&
                    record.PUNCHOUT !== "" &&
                    !record.PUNCHOUT.includes("1900-01-01");

                  return (
                    <tr key={record.ID || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <input
                          type="checkbox"
                          value={record.ID}
                          checked={getItemCheckedStatus(record.ID)}
                          onChange={handleIndividualCheck}
                          disabled={record?.LeaveStatus == 1}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {record.EmployeeName || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.EmpCode || "N/A"}
                      </td>
                      {isAdmin == 1 && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.DepartmentName || "N/A"}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.ShiftName || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatTime(record.StartTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatTime(record.EndTime)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          !punchInValid ? "text-red-500" : "text-gray-500"
                        }`}
                      >
                        {formatTime(record.PUNCHIN)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          !punchOutValid ? "text-red-500" : "text-gray-500"
                        }`}
                      >
                        {formatTime(record.PUNCHOUT)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {filteredData.length > 0 && (
            <div className="p-4">
              <button
                onClick={handleAppproveAttendacne}
                disabled={
                  loading ||
                  individualCheck.filter((item) => item.ATTENDANCESTATUS === 1)
                    .length === 0
                }
                className="bg-blue hover:bg-blue disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded"
              >
                {loading ? "Processing..." : "Approve Selected Attendance"}
                {individualCheck.filter((item) => item.ATTENDANCESTATUS === 1)
                  .length > 0 &&
                  ` (${
                    individualCheck.filter(
                      (item) => item.ATTENDANCESTATUS === 1,
                    ).length
                  })`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AttendancePending;
